import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Users } from "lucide-react";
import { api } from "@/lib/api";
import { PageMeta } from "@/components/common/PageMeta";
import { PageHero } from "@/components/common/PageHero";
import { HERO_ALT } from "@/lib/content";

const ASSEMBLY = ["Challenge map", "Capability graph", "Conflict / availability review", "Scoped team", "Governed workspace"];
const SELECTION = ["Relevance", "Evidence", "Availability", "Independence", "Confidentiality", "Project constraints"];

export default function Network() {
  const [agg, setAgg] = useState(null);
  useEffect(() => {
    api.get("/network/aggregate").then(({ data }) => setAgg(data));
  }, []);

  return (
    <>
      <PageMeta title="Network" description="A structured capability map used to form responsible project teams." />
      <PageHero
        eyebrow="The professional network"
        title="A professional network organised around contribution."
        intro="DBRF brings together specialists whose work becomes more valuable when connected to adjacent fields. The network is not a public directory of personal data. It is a structured capability map used to form responsible project teams."
        image="/assets/04-network-hero.webp"
        alt={HERO_ALT.network}
        objectPosition="center"
        cta={[{ to: "/join-the-network", label: "Apply to join the network", primary: true, testid: "network-hero-cta" }]}
      />

      <section className="container-dbrf py-16 lg:py-24">
        <p className="eyebrow mb-4">Network explorer</p>
        <h2 className="font-display font-bold text-navy" style={{ fontSize: "clamp(1.8rem,3vw,2.6rem)" }}>Aggregate views, not personal data.</h2>
        <div className="mt-6 rounded-2xl border border-dashed border-[var(--line)] bg-[var(--paper)] p-8 text-center" data-testid="network-empty-state">
          <Users size={28} className="mx-auto text-[var(--mineral)]" />
          <p className="mt-4 font-display text-lg text-navy">{agg?.message || "Profiles will appear as the verified network grows."}</p>
          <p className="mt-2 text-sm text-[var(--mineral)]">Aggregate counts are shown only when real, opt-in records exist.</p>
        </div>

        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {[["Disciplines", agg?.disciplines], ["Domains", agg?.domains], ["Methods", agg?.methods]].map(([label, items]) => (
            <div key={label} className="rounded-2xl border border-[var(--line)] bg-[var(--paper)] p-6">
              <p className="eyebrow mb-3">{label}</p>
              <div className="flex flex-wrap gap-1.5">
                {(items || []).slice(0, 8).map((x) => <span key={x} className="rounded-full border border-[var(--line)] bg-[var(--ivory)] px-2.5 py-1 font-mono text-[0.66rem] text-[var(--mineral)]">{x}</span>)}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="border-y border-[var(--line)] bg-[var(--paper)] py-16 lg:py-24" data-testid="capabilities-sought">
        <div className="container-dbrf">
          <p className="eyebrow mb-4">Capabilities currently sought</p>
          <h2 className="font-display font-bold text-navy" style={{ fontSize: "clamp(1.6rem,2.6vw,2.4rem)" }}>Where new contributors can help.</h2>
          <p className="mt-3 max-w-2xl text-[var(--mineral)]">Drawn from the skills the current OEM portfolio requires. No counts are published unless backed by approved records.</p>
          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {(agg?.capabilities_sought || []).map((g) => (
              <div key={g.group} className="rounded-2xl border border-[var(--line)] bg-[var(--ivory)] p-6">
                <p className="font-display font-semibold text-navy">{g.group}</p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {g.skills.map((s) => <span key={s} className="rounded-full border border-[var(--line)] bg-[var(--paper)] px-2.5 py-1 font-mono text-[0.64rem] text-[var(--mineral)]">{s}</span>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container-dbrf py-16 lg:py-24">
          <p className="eyebrow mb-4">Team assembly</p>
          <h2 className="font-display font-bold text-navy" style={{ fontSize: "clamp(1.6rem,2.6vw,2.4rem)" }}>How a team is formed.</h2>
          <div className="mt-10 flex flex-col gap-3 lg:flex-row lg:items-center">
            {ASSEMBLY.map((s, i) => (
              <div key={s} className="flex items-center gap-3">
                <div className="rounded-xl border border-[var(--line)] bg-[var(--ivory)] px-4 py-3 text-sm font-medium text-navy">{s}</div>
                {i < ASSEMBLY.length - 1 && <ArrowRight size={16} className="hidden flex-shrink-0 text-[var(--cobalt)] lg:block" />}
              </div>
            ))}
          </div>
          <p className="mt-8 text-[var(--mineral)]">DBRF selection is based on:</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {SELECTION.map((s) => <span key={s} className="rounded-full border border-[var(--line)] px-3 py-1 text-sm text-navy">{s}</span>)}
          </div>
          <Link to="/join-the-network" data-testid="network-apply-cta" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[var(--navy)] px-6 py-3.5 text-sm font-semibold text-white btn-transition hover:bg-[var(--cobalt)]">Apply to join the network <ArrowRight size={16} /></Link>
      </section>
    </>
  );
}
